import React, { useState } from 'react';
import { apiService } from '../services/api';
import { useApi } from '../hooks/useApi';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { ShoppingBag, Eye, Calendar, Search, X, ArrowLeft, ArrowRight, XCircle, Package } from 'lucide-react';
import Select from 'react-select';
import LoadingSpinner from '../components/LoadingSpinner';

function PorositeOnline() {
  const { theme } = useTheme();
  const { roli } = useAuth();
  const { getCurrency, getCurrencyCode } = useSettings();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [lokacionFilter, setLokacionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPorosia, setSelectedPorosia] = useState(null);
  const [loadingDetaje, setLoadingDetaje] = useState(false);

  // Fetch lokacionet për filter (vetëm për admin)
  const { data: lokacionetData } = useApi(
    () => apiService.get('/lokacionet'),
    [],
    { immediate: roli === 'admin', showNotifications: false }
  );

  const lokacionet = Array.isArray(lokacionetData) ? lokacionetData : (lokacionetData?.data || []);

  // Fetch porositë online me pagination
  const getParams = () => {
    const params = {
      page: currentPage,
      per_page: perPage,
    };
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (roli === 'admin' && lokacionFilter) params.lokacion_id = lokacionFilter;
    if (searchTerm) params.search = searchTerm;
    return params;
  };

  const { data: porositeData, loading, execute: refetchPorosite } = useApi(
    () => apiService.get('/porosite-online', { params: getParams() }),
    [currentPage, fromDate, toDate, lokacionFilter, searchTerm],
    { immediate: true, showNotifications: false }
  );

  const porosite = porositeData?.data || [];
  const pagination = porositeData?.pagination || {};

  // Fetch detajet e porosisë së zgjedhur
  const handleViewDetaje = async (porosiaId) => {
    setLoadingDetaje(true);
    try {
      const detaje = await apiService.get(`/porosite-online/${porosiaId}`);
      setSelectedPorosia(detaje);
    } catch (error) {
      console.error('Gabim gjatë marrjes së detajeve:', error);
    } finally {
      setLoadingDetaje(false);
    }
  };

  const closeModal = () => {
    setSelectedPorosia(null);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setLokacionFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency - merr monedhën nga settings
  const formatCurrency = (amount) => {
    // Merr kodin e monedhës direkt nga settings përmes getCurrencyCode()
    const currencyCode = getCurrencyCode();
    
    // Formatizo si faqet e tjera - përdor .toFixed(2) dhe shto kodin e monedhës
    return `${Number(amount).toFixed(2)} ${currencyCode}`;
  };

  // Custom styles for react-select
  const selectStyles = theme === 'dark' ? {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#f59e0b' : '#475569',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: '#475569',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#78350f' : state.isSelected ? '#f59e0b' : '#1e293b',
      color: state.isSelected ? 'white' : '#e2e8f0',
    }),
    singleValue: (base) => ({ ...base, color: '#e2e8f0' }),
    input: (base) => ({ ...base, color: '#e2e8f0' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  } : {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#fef3c7' : state.isSelected ? '#fbbf24' : 'white',
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  const lokacionOptions = lokacionet.map(l => ({
    value: l.id,
    label: l.emri
  }));

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <ShoppingBag size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Porositë Online
      </h2>

      {/* Filters */}
      <div className="card-premium p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Kërko kupon kodi ose ID porosie..."
              className="input-premium pl-10"
            />
          </div>

          {/* From Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
              className="input-premium pl-10"
            />
          </div>

          {/* To Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
              className="input-premium pl-10"
            />
          </div>

          {/* Lokacion Filter - vetëm për admin */}
          {roli === 'admin' && (
            <Select
              placeholder="Lokacioni"
              isClearable
              value={lokacionOptions.find(o => o.value === lokacionFilter) || null}
              onChange={(option) => {
                setLokacionFilter(option ? option.value : '');
                setCurrentPage(1);
              }}
              options={lokacionOptions}
              styles={selectStyles}
              classNamePrefix="react-select"
            />
          )}
        </div>

        {/* Reset Button */}
        {(fromDate || toDate || lokacionFilter || searchTerm) && (
          <button
            onClick={handleResetFilters}
            className="mt-4 btn-warning flex items-center gap-2"
          >
            <X size={18} />
            Pastro Filtra
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card-premium overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table-premium">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ID Porosie</th>
                    <th>Kupon Kodi</th>
                    <th>Data</th>
                    {roli === 'admin' && <th>Lokacioni</th>}
                    <th>Nr. Produkteve</th>
                    <th>Totali</th>
                    <th>Veprime</th>
                  </tr>
                </thead>
                <tbody>
                  {porosite.length === 0 ? (
                    <tr>
                      <td colSpan={roli === 'admin' ? 8 : 7} className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col items-center gap-3">
                          <Package size={48} className="text-gray-300 dark:text-dark-600" />
                          <p className="text-lg">Nuk u gjetën porosi online</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    porosite.map((porosia, index) => (
                      <tr key={porosia.id} className="hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors">
                        <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                        <td className="font-mono font-medium text-primary-600 dark:text-primary-400">
                          #{porosia.woo_order_id || '-'}
                        </td>
                        <td className="font-medium font-mono">{porosia.kupon_kodi || '-'}</td>
                        <td>{formatDate(porosia.data || porosia.created_at)}</td>
                        {roli === 'admin' && (
                          <td>{porosia.lokacion?.emri || '-'}</td>
                        )}
                        <td className="text-center">
                          <span className="badge-primary">{porosia.numri_produkteve || 0}</span>
                        </td>
                        <td className="font-bold text-primary-600 dark:text-primary-400">
                          {formatCurrency(porosia.totali)}
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewDetaje(porosia.id)}
                            className="btn-primary flex items-center gap-2"
                            title="Shiko Detaje"
                          >
                            <Eye size={16} />
                            Shiko
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800/50">
                <div className="text-sm text-gray-600 dark:text-dark-400">
                  Duke shfaqur {pagination.from || 0} - {pagination.to || 0} nga {pagination.total || 0} porosi
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={pagination.current_page === 1}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ArrowLeft size={16} />
                    Para
                  </button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-300">
                    Faqja {pagination.current_page} nga {pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
                    disabled={pagination.current_page === pagination.last_page}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Pas
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal për Detajet */}
      {selectedPorosia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm px-4 animate-fadeIn">
          <div className="relative card-premium w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-slide-down p-6">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              aria-label="Mbylle"
            >
              <XCircle size={26} />
            </button>

            {loadingDetaje ? (
              <div className="flex justify-center items-center py-20">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="mb-6 border-b border-gray-200 dark:border-dark-700 pb-4">
                  <h3 className="text-2xl font-bold text-gradient-logo mb-2">
                    Detajet e Porosisë Online
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">ID Porosie</p>
                      <p className="font-mono font-bold text-primary-600 dark:text-primary-400">
                        #{selectedPorosia.woo_order_id || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Kupon Kodi</p>
                      <p className="font-mono font-bold">{selectedPorosia.kupon_kodi || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Data</p>
                      <p className="font-medium">{formatDate(selectedPorosia.data || selectedPorosia.created_at)}</p>
                    </div>
                    {roli === 'admin' && (
                      <div>
                        <p className="text-gray-500 dark:text-dark-400">Lokacioni</p>
                        <p className="font-medium">{selectedPorosia.lokacion?.emri || '-'}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Detajet e Produkteve */}
                <div className="mb-6">
                  <h4 className="text-lg font-bold mb-4 text-gray-700 dark:text-dark-300">
                    Produktet e Porosisë
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="table-premium">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Produkti</th>
                          <th>Madhësia</th>
                          <th>Ngjyra</th>
                          <th>Sasia</th>
                          <th>Çmimi</th>
                          <th>Nëntotali</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPorosia.detaje?.length > 0 ? (
                          selectedPorosia.detaje.map((detaje, index) => (
                            <tr key={detaje.id}>
                              <td>{index + 1}</td>
                              <td className="font-medium">
                                {detaje.produkt?.emri || '-'}
                                {detaje.produkt?.kodi && (
                                  <span className="text-xs text-gray-500 dark:text-dark-400 ml-2">
                                    ({detaje.produkt.kodi})
                                  </span>
                                )}
                              </td>
                              <td>{detaje.madhesi?.emri || '-'}</td>
                              <td>{detaje.ngjyra?.emri || '-'}</td>
                              <td className="text-center">{detaje.sasia}</td>
                              <td>{formatCurrency(detaje.cmimi_shitjes)}</td>
                              <td className="font-bold text-primary-600 dark:text-primary-400">
                                {formatCurrency(detaje.sasia * detaje.cmimi_shitjes)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-8 text-gray-500 dark:text-gray-400">
                              Nuk ka detaje
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totali */}
                <div className="flex justify-end border-t border-gray-200 dark:border-dark-700 pt-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-dark-400 mb-2">Totali:</p>
                    <p className="text-3xl font-bold text-gradient-logo">
                      {formatCurrency(selectedPorosia.totali)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PorositeOnline;

